import {
    autoUpdate,
    flip,
    FloatingFocusManager,
    FloatingOverlay,
    FloatingPortal,
    offset,
    Placement,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useMergeRefs,
    useRole,
} from '@floating-ui/react'
import { AnimatePresence, motion } from 'motion/react'
import { cloneElement, isValidElement, MouseEventHandler, ReactNode, Ref, useState } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import { FLOATING_MOTION_PROPS, FLOATING_OVERLAY_MOTION_PROPS, TRANSFORM_ORIGIN_BY_PLACEMENT } from '../Utilities/floatingMotion'

export type PopoverSide = 'top' | 'bottom' | 'left' | 'right'
export type PopoverAlign = 'start' | 'center' | 'end'

export type NativePopoverProps = {
    /** The element to trigger the popover. React components must spread props and pass the ref prop to their underlying element. */
    children?: ReactNode
    /** The content to display inside the popover. */
    popoverContent: ReactNode
    /** Callback function that gets called when the popover open state changes. */
    onOpenChange?: (open: boolean) => void
    /** Called after the closing animation has removed the panel. */
    onExitComplete?: () => void
    /** Use `click` when outside pointerdown handlers must finish before dismissal. */
    outsidePressEvent?: 'pointerdown' | 'mousedown' | 'click'
    /** Remove padding from the popover content. */
    noPadding?: boolean
    /**
     * Trap focus in the panel. Defaults to true, which is what a form or a confirmation wants. Pass false
     * when the panel is a picker inside a surface the user keeps working in, or when other triggers on the
     * screen must stay clickable — Tab then leaves the panel instead of cycling inside it.
     */
    isModal?: boolean
    /** Control the open state of the popover. */
    isOpen?: boolean
    /** A data-test attribute on the trigger element. */
    'data-test'?: string
    /**
     * Accessible name for the panel. The panel is a `role="dialog"`, so without a name a screen reader
     * announces it as an unnamed dialog and the user has to explore it to learn what opened. Name it after
     * the trigger: a "Filters" button opens a "Filters" panel.
     */
    'aria-label'?: string
    /**
     * Id of the element inside the panel that already names it, such as a heading. Takes precedence over
     * `aria-label`, so pass one or the other.
     */
    'aria-labelledby'?: string
    /** Show an overlay behind the popover content. Defaults to false. */
    showOverlay?: boolean
    /** Disable the popover trigger. */
    disabled?: boolean
    /** Disable animations. */
    reducedMotion?: boolean
    /** Additional className for the popover content container. */
    className?: string
    /**
     * The preferred side of the trigger to render the popover: `top`, `bottom`, `left` or `right`. Defaults
     * to `bottom`. A preference, not a guarantee — the panel flips and shifts rather than leave the viewport.
     */
    side?: PopoverSide
    /**
     * The preferred alignment along that side: `start`, `center` or `end`. Defaults to `center`. `align` runs
     * along the side the panel sits on, so it moves the panel horizontally under `bottom` and vertically
     * beside `right`.
     */
    align?: PopoverAlign
    /** The distance in pixels from the trigger. */
    sideOffset?: number
    /**
     * Specify a container element to portal the content into.
     */
    container?: HTMLElement | null
    /**
     * Position against an externally selected element instead of a trigger child.
     */
    anchorElement?: HTMLElement | null
    /** If true, the popover will allow click and keydown events to propagate to parent elements. */
    allowEventPropagation?: boolean
    /** If true, the popover will match the width of the trigger element. */
    matchTriggerWidth?: boolean
}

const DEFAULT_SIDE_OFFSET = 5

/** FloatingOverlay renders a plain div, so it needs wrapping to take part in the enter and exit animations. */
const MotionFloatingOverlay = motion.create(FloatingOverlay)

type TriggerElementProps = {
    ref?: Ref<Element>
    onClick?: MouseEventHandler<Element>
    [key: string]: unknown
}

/**
 * Convert side and align to FloatingUI placement
 */
function getPlacement(side: PopoverSide, align: PopoverAlign): Placement {
    if (align === 'center') return side as Placement
    return `${side}-${align}` as Placement
}

/**
 * Does a thing. Opens on click, takes focus, anchored to its trigger. Click is deliberate, so this is the
 * only floating panel that may hold inputs, actions or state.
 *
 * Which one: one plain string is an AstraTooltip; structured read-only content is an AstraHoverCard; a plain
 * list of actions is an AstraDropdownMenu; a destructive confirmation is an AstraAlertDialog. Over 480px of
 * content, or more than one step, is an AstraDialog or AstraDrawer.
 *
 * Rules: never opens on hover — one trigger, one behaviour. A tooltip naming the trigger is fine, with the
 * AstraTooltip on the outside, since it passes only its own `data-tooltip` attributes through and a popover
 * wrapped the other way round never receives the click handler that opens it. Outside click and Esc both
 * dismiss the panel, so autosave on close or confirm first. Only controls this panel owns: a picker, select
 * or menu inside is fine, a second panel with its own content and dismissal is not, and a modal Dialog
 * inside means the whole thing should have been a Dialog. Clicks inside the panel never dismiss it, so a
 * checkbox list or a multi-step selection can be worked through before the user closes it.
 *
 * The Astra/Popover docs page carries the full selection guide.
 */
export const AstraPopover = ({
    children,
    popoverContent,
    onOpenChange,
    onExitComplete,
    outsidePressEvent,
    noPadding = false,
    showOverlay = false,
    isOpen,
    'data-test': dataTest,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    disabled,
    isModal = true,
    reducedMotion = false,
    className,
    side = 'bottom',
    align = 'center',
    sideOffset = DEFAULT_SIDE_OFFSET,
    matchTriggerWidth = false,
    container,
    anchorElement,
    allowEventPropagation = true,
}: NativePopoverProps) => {
    const isControlled = isOpen !== undefined
    const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
    const currentOpen = isControlled ? isOpen : uncontrolledOpen

    const setOpen = (next: boolean) => {
        if (!isControlled) setUncontrolledOpen(next)
        onOpenChange?.(next)
    }

    const { x, y, strategy, refs, context, elements, placement } = useFloating({
        open: currentOpen,
        onOpenChange: setOpen,
        elements: { reference: anchorElement },
        placement: getPlacement(side, align),
        whileElementsMounted: autoUpdate,
        middleware: [offset({ mainAxis: sideOffset }), flip(), shift({ padding: { right: 28, left: 28 } })],
    })

    const click = useClick(context)
    const dismiss = useDismiss(context, { outsidePressEvent })
    const role = useRole(context, { role: 'dialog' })
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

    const triggerRef = isValidElement<TriggerElementProps>(children) ? children.props.ref : undefined
    const setTriggerRef = useMergeRefs<Element>([anchorElement ? undefined : refs.setReference, triggerRef])

    if (disabled) return <>{children}</>
    if (!anchorElement && !isValidElement<TriggerElementProps>(children)) return <>{children}</>

    const referenceProps = isValidElement<TriggerElementProps>(children)
        ? getReferenceProps({
              ...children.props,
              onClick: event => {
                  children.props.onClick?.(event)
                  if (!allowEventPropagation) event.stopPropagation()
              },
          })
        : undefined

    return (
        <>
            {isValidElement<TriggerElementProps>(children) &&
                cloneElement(children, {
                    ...referenceProps,
                    'data-test': dataTest ?? children.props['data-test'],
                    ref: setTriggerRef,
                })}

            <AnimatePresence onExitComplete={onExitComplete}>
                {currentOpen && (
                    <FloatingPortal root={container}>
                        {showOverlay && <MotionFloatingOverlay {...(reducedMotion ? {} : FLOATING_OVERLAY_MOTION_PROPS)} className="z-elevated bg-black/25" />}
                        <FloatingFocusManager context={context} modal={isModal}>
                            <motion.div
                                ref={refs.setFloating}
                                tabIndex={-1}
                                {...(reducedMotion ? {} : FLOATING_MOTION_PROPS)}
                                style={{
                                    position: strategy,
                                    top: y,
                                    left: x,
                                    width: matchTriggerWidth ? elements.reference?.getBoundingClientRect().width : undefined,
                                }}
                                className={astraTwMerge(
                                    'z-elevated rounded-xl border border-base bg-surface-base shadow-popover outline-none',
                                    TRANSFORM_ORIGIN_BY_PLACEMENT[placement],
                                    noPadding ? '' : 'p-3',
                                    className
                                )}
                                aria-label={ariaLabel}
                                aria-labelledby={ariaLabelledBy}
                                {...getFloatingProps(
                                    allowEventPropagation
                                        ? {}
                                        : {
                                              onClick: event => {
                                                  event.stopPropagation()
                                              },
                                              onKeyDown: event => {
                                                  event.stopPropagation()
                                              },
                                          }
                                )}
                            >
                                {popoverContent}
                            </motion.div>
                        </FloatingFocusManager>
                    </FloatingPortal>
                )}
            </AnimatePresence>
        </>
    )
}
